// src/hooks/useAuthInfo.ts
'use client';

import { useAuthStore } from '@/state/authState';
import { useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { Session } from 'next-auth';
import { useSelfHostedManagerOrgs } from '@/hooks/useSelfHostedManagerOrgs';

export function useAuthInfo() {
  const { data: session, status } = useSession();
  const {
    setSession,
    setStatus,
    setAuthUrl,
    authUrl,
    providers,
    actualProvider,
    getAuthUrl,
    setActualProvider,
  } = useAuthStore();

  // Only fetch self-hosted orgs when actually needed:
  // - User is authenticated with a non-standard provider (self-hosted GitLab)
  // Most users use github/gitlab, so we skip the fetch for them to avoid unnecessary API calls
  const isStandardProvider =
    session?.user?.provider === 'github' ||
    session?.user?.provider === 'gitlab';
  const shouldFetchSelfHosted =
    status === 'authenticated' && !isStandardProvider;

  // Use React Query hook to fetch providers - only when needed
  const { data: providerResources } = useSelfHostedManagerOrgs(
    shouldFetchSelfHosted
  );

  useEffect(() => {
    setSession(session as Session);
    setStatus(status);
  }, [session, status, setSession, setStatus]);

  // Sync React Query data to Zustand store
  useEffect(() => {
    if (providerResources) {
      const providersMap: Record<string, any> = {};
      providerResources.forEach((provider) => {
        providersMap[provider.id] = provider;
      });
      useAuthStore.setState({ providers: providersMap });
    }
  }, [providerResources]);

  useEffect(() => {
    if (providers && session && status === 'authenticated') {
      try {
        if (
          session.user?.provider === 'github' ||
          session.user?.provider === 'gitlab'
        )
          return;
        const providerKey = session.user?.provider;
        if (!providerKey) {
          return;
        }

        const provider = providers[providerKey];
        if (provider) {
          const authUrl =
            typeof (provider as { baseUrl?: string }).baseUrl === 'string' &&
            (provider as { baseUrl?: string }).baseUrl
              ? (provider as { baseUrl: string }).baseUrl
              : typeof provider.name === 'string' &&
                  provider.name.startsWith('self-hosted-')
                ? provider.name.slice('self-hosted-'.length)
                : '';
          setAuthUrl(authUrl);
          setActualProvider(provider as Record<string, any>);
        }
      } catch (error) {
        console.error('Error setting auth url:', error);
      }
    }
  }, [providers, session, status, setAuthUrl, setActualProvider]);

  return {
    session,
    status,
    authUrl,
    providers,
    domain_url: getAuthUrl(session?.user?.provider ?? ''),
    provider: actualProvider,
    isLoading: status === 'loading',
    isAuthenticated: status === 'authenticated',
  };
}
